import { Card } from '@guridosdrones/ui';
import { animate, motion, useInView, useMotionValue, useTransform } from 'framer-motion';
import { Award, BookOpen, Target, Users } from 'lucide-react';
import { useEffect, useRef } from 'react';
import eduardo from '@/assets/eduardo.jpg';
import { FadeIn } from '@/components';

function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (v) => Math.round(v));

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(motionValue, value, { duration: 1.8, ease: 'easeOut' });
    return controls.stop;
  }, [isInView, motionValue, value]);

  useEffect(() => {
    const unsubscribe = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = `${v.toLocaleString('pt-BR')}${suffix}`;
    });
    return unsubscribe;
  }, [rounded, suffix]);

  return <span ref={ref}>0{suffix}</span>;
}

export function Instructor() {
  const achievements = [
    {
      icon: Users,
      title: '+90 Mil Seguidores',
      description: 'O maior influenciador brasileiro no segmento de drones de pulverização.',
    },
    {
      icon: Target,
      title: 'Prática no Campo',
      description: 'Agricultor que testa pessoalmente DJI T10, T25 e XAG P100 Pro.',
    },
    {
      icon: BookOpen,
      title: 'Dicas Exclusivas',
      description: 'Expertise real conectada com os desafios do dia a dia da lavoura.',
    },
  ];

  return (
    <section id="instructor" className="section-shell bg-background">
      {/* Background decoration */}
      <div className="absolute top-1/4 -right-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

      <div className="section-container">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-18">
          {/* Image side */}
          <div className="flex-1 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <div className="relative aspect-4/5 overflow-hidden border border-border/70 bg-muted shadow-2xl">
                <img
                  src={eduardo}
                  alt="Eduardo dos Anjos - Guri dos Drones"
                  className="w-full h-full object-cover grayscale-20 contrast-[1.1]"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />

                {/* Corner brackets */}
                <svg
                  className="absolute top-3 left-3 h-5 w-5 text-primary/40"
                  viewBox="0 0 20 20"
                  fill="none"
                  role="img"
                  aria-label="Decoração"
                >
                  <path d="M0 6V0h6" stroke="currentColor" strokeWidth="1" />
                </svg>
                <svg
                  className="absolute top-3 right-3 h-5 w-5 text-primary/40"
                  viewBox="0 0 20 20"
                  fill="none"
                  role="img"
                  aria-label="Decoração"
                >
                  <path d="M20 6V0h-6" stroke="currentColor" strokeWidth="1" />
                </svg>

                <div className="absolute bottom-8 left-8">
                  <p className="mb-1 font-mono text-[10px] tracking-[0.2em] text-primary/70 uppercase">
                    Operador Principal
                  </p>
                  <p className="text-white text-2xl font-bold font-heading">Eduardo dos Anjos</p>
                </div>
              </div>

              {/* Floating Badge */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-6 -right-6 z-20 hidden items-center gap-3 border border-border bg-card p-4 shadow-xl sm:flex md:-right-10"
              >
                <div className="border border-primary/20 bg-primary/10 p-2 text-primary">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-mono text-[9px] text-muted-foreground uppercase tracking-[0.15em] leading-none">
                    Autoridade
                  </p>
                  <p className="font-bold text-foreground">Guri dos Drones</p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Text content side */}
          <div className="flex flex-1 flex-col gap-7">
            <FadeIn direction="right" distance={20}>
              <div className="flex flex-col gap-6">
                <p className="font-mono text-xs tracking-[0.25em] text-primary/70 uppercase">
                  Perfil do Piloto
                </p>

                <h2 className="section-title">
                  Eduardo dos Anjos <br />
                  <span className="text-primary text-2xl sm:text-3xl lg:text-4xl">
                    O Guri dos Drones
                  </span>
                </h2>

                <div className="flex flex-col gap-4">
                  <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                    O Guri dos Drones é o <strong>maior influenciador brasileiro</strong> no
                    segmento de drones de pulverização, com mais de 90 mil seguidores nas redes
                    sociais.
                  </p>

                  <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                    Além de influenciador, ele é <strong>agricultor</strong>, o que lhe permite
                    testar pessoalmente diversos modelos de drones no campo, como os DJI T10, DJI
                    T25 e o XAG P100 Pro.
                  </p>

                  <p className="hidden border-l-2 border-primary/30 pl-4 text-base leading-relaxed text-muted-foreground italic sm:block sm:text-lg">
                    "Ficou famoso por suas 'loucuras' - como plantar soja e trigo com drones e até
                    fazer chá revelação!"
                  </p>

                  <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
                    No curso, você terá acesso à sua expertise e dicas exclusivas, tornando o
                    aprendizado ainda mais completo e conectado com a{' '}
                    <strong>realidade do campo</strong>.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Animated Stats */}
            <FadeIn direction="up" distance={15} delay={0.1}>
              <div className="grid grid-cols-3 gap-4 border border-border bg-card p-5 sm:p-6">
                <div className="text-center">
                  <p className="text-2xl font-black text-foreground font-heading sm:text-3xl">
                    <AnimatedNumber value={90} suffix="mil+" />
                  </p>
                  <p className="mt-1 font-mono text-[9px] tracking-widest text-muted-foreground uppercase">
                    Seguidores
                  </p>
                </div>
                <div className="border-x border-border text-center">
                  <p className="text-2xl font-black text-foreground font-heading sm:text-3xl">
                    <AnimatedNumber value={3} suffix="+" />
                  </p>
                  <p className="mt-1 font-mono text-[9px] tracking-widest text-muted-foreground uppercase">
                    Modelos Testados
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-black text-foreground font-heading sm:text-3xl">
                    <AnimatedNumber value={500} suffix="+" />
                  </p>
                  <p className="mt-1 font-mono text-[9px] tracking-widest text-muted-foreground uppercase">
                    Alunos Formados
                  </p>
                </div>
              </div>
            </FadeIn>

            <div className="grid gap-5">
              {achievements.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-5 bg-card border-border hover:border-primary/30 transition-all duration-300 flex gap-4 items-start group">
                    <div className="p-3 border border-primary/15 bg-primary/8 text-primary group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
